import { evictMaterial } from "../../utils";
export class TileCache {
    constructor(tilingScheme) {
        this.cache = [];
        this.tilingScheme = tilingScheme;
        // 32 zoom levels as we'll likely never get data refined to a higher level than that
        for (let i = 0; i < 32; i += 1) {
            this.cache.push(new Map());
        }
    }
    addItem(x, y, zoomLevel, item) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        const cacheItem = Object.assign({}, item);
        this.cache[zoomLevel].set(hash, cacheItem);
        return cacheItem;
    }
    /**
     * This function will return a ThreeJS 'Mesh' object for the requested tile, or undefined if no mesh has been
     * generated for that tile.
     *
     * This function allows getting map tiles without triggering tile generation. This allows for querying if a tile is
     * resident, or getting a tile to render only if the data is already loaded.
     *
     * @param zoomLevel The zoom level we're looking up a tile at
     * @param x The x coordinate of the tile to get
     * @param y The y coordinate of the tile to get
     */
    acquire(x, y, zoomLevel) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        return this.cache[zoomLevel].get(hash);
    }
    /**
     * This function will evict the cached data for the given tile. If there is no cache entry for the tile then the
     * function will just do nothing.
     * @param zoomLevel The zoom level we're looking up a tile to evict
     * @param x The x coordinate of the tile to evict
     * @param y The y coordinate of the tile to evict
     */
    release(x, y, zoomLevel) {
        const hash = this.hashTileCoordinateForNode(x, y, zoomLevel);
        const mesh = this.cache[zoomLevel].get(hash);
        if (mesh !== undefined) {
            // Evict the material/texture data, then evict the mesh item
            evictMaterial(mesh.material);
            this.cache[zoomLevel].delete(hash);
        }
    }
    /**
     * This provides a simple and efficient hash function for a zoomLevel + tileCoordinate pair that will provide a
     * unique integer for any coordinate at a given zoom level.
     *
     * All inputs *MUST* be integers. No guarantees on the output are made if *any* of the input values are not whole
     * numbers.
     *
     * This hash function is intended to be used for the node cache.
     *
     * WARNING
     *
     * There are no uniqueness guarantees *between zoom levels*. hashTileCoordinate(1, 0, 1) and
     * hashTileCoordinate(1, 0, 2) *WILL* hash to the same value. The uniqueness of the hash is only held at the same
     * zoom level.
     * @param x The x coordinate of the tile (horizontal)
     * @param y The y coordinate of the tile (vertical)
     * @param zoomLevel The zoom level we're hashing for
     */
    hashTileCoordinateForNode(x, y, zoomLevel) {
        const width = this.tilingScheme.getNumberOfXTilesAtLevel(zoomLevel);
        return width * y + x;
    }
}
